import { makeChoices } from "../../distractors";
import { text } from "../../format";
import type { QuestionDraft, Rng, TopicDef } from "../../types";
import { both, numeric, q } from "../helpers";

function tier1(rng: Rng): QuestionDraft {
  const a = rng.int(1, 9);
  let b = rng.int(1, 9);
  if (b === a) b = a + 1;
  const k = rng.int(2, 6);
  const d = b * k;
  const x = a * k;
  const choice4 = makeChoices(rng, x, [a + (d - b), x + 1, d - a, a * d], (r) => r.int(1, 60));
  return q(
    both(`Uzupełnij proporcję: ${a} : ${b} = ? : ${d}`, `Complete the proportion: ${a} : ${b} = ? : ${d}`),
    numeric(x),
    [`Druga liczba wzrosła ${k} razy (${b} → ${d}), więc pierwsza też: ${a} · ${k}.`, `Odpowiedź: ${x}.`],
    [
      `The second number grew ${k} times (${b} → ${d}), so the first one does too: ${a} × ${k}.`,
      `Answer: ${x}.`,
    ],
    { choice4 },
  );
}

function tier2(rng: Rng): QuestionDraft {
  const a = rng.int(2, 12);
  let b = rng.int(2, 12);
  if (b === a) b = a + 2;
  const k = rng.int(3, 9);
  const c = a * k;
  const x = b * k;
  const choice4 = makeChoices(rng, x, [b + (c - a), x - b, c - b, x + k], (r) => r.int(1, 120));
  return q(
    both(`Uzupełnij proporcję: ${a} : ${b} = ${c} : ?`, `Complete the proportion: ${a} : ${b} = ${c} : ?`),
    numeric(x),
    [
      `Sprawdzamy, ile razy wzrosła pierwsza liczba: ${c} : ${a} = ${k}.`,
      `Druga liczba: ${b} · ${k} = ${x}.`,
    ],
    [`Check how many times the first number grew: ${c} ÷ ${a} = ${k}.`, `Second number: ${b} × ${k} = ${x}.`],
    { choice4 },
  );
}

function tier3(rng: Rng): QuestionDraft {
  if (rng.chance(0.5)) {
    const lemonsPer = rng.int(2, 6);
    const per = rng.int(2, 4);
    const k = rng.int(2, 6);
    const want = per * k;
    const result = lemonsPer * k;
    const choice4 = makeChoices(rng, result, [lemonsPer + want, result + lemonsPer, want * per], (r) =>
      r.int(2, 60),
    );
    return q(
      both(
        `Finn zużywa ${lemonsPer} cytryn na ${per} Lemon Splashe. Ile cytryn potrzebuje na ${want} Splashów?`,
        `Finn uses ${lemonsPer} lemons for ${per} Lemon Splashes. How many lemons does he need for ${want} Splashes?`,
      ),
      numeric(result),
      [
        `${want} : ${per} = ${k}, czyli ${k} razy więcej Splashów.`,
        `Cytryn też ${k} razy więcej: ${lemonsPer} · ${k} = ${result}.`,
      ],
      [
        `${want} ÷ ${per} = ${k}, so ${k} times more Splashes.`,
        `Also ${k} times more lemons: ${lemonsPer} × ${k} = ${result}.`,
      ],
      { choice4 },
    );
  }
  const p1 = rng.int(1, 4);
  let p2 = rng.int(1, 5);
  if (p2 === p1) p2 = p1 + 1;
  const unit = rng.int(2, 8);
  const total = (p1 + p2) * unit;
  const askSecond = rng.chance(0.5);
  const result = (askSecond ? p2 : p1) * unit;
  const choice4 = makeChoices(rng, result, [(askSecond ? p1 : p2) * unit, total / 2, total - unit], (r) =>
    r.int(1, 80),
  );
  return q(
    both(
      `Podziel ${total} monet w stosunku ${p1} : ${p2}. Ile monet dostanie ${askSecond ? "druga" : "pierwsza"} osoba?`,
      `Share ${total} gold in the ratio ${p1} : ${p2}. How much does the ${askSecond ? "second" : "first"} person get?`,
    ),
    numeric(result),
    [
      `Wszystkich części jest ${p1} + ${p2} = ${p1 + p2}.`,
      `Jedna część: ${total} : ${p1 + p2} = ${unit}.`,
      `${askSecond ? p2 : p1} części: ${askSecond ? p2 : p1} · ${unit} = ${result}.`,
    ],
    [
      `There are ${p1} + ${p2} = ${p1 + p2} parts in total.`,
      `One part: ${total} ÷ ${p1 + p2} = ${unit}.`,
      `${askSecond ? p2 : p1} parts: ${askSecond ? p2 : p1} × ${unit} = ${result}.`,
    ],
    { choice4 },
  );
}

export const ratios: TopicDef = {
  id: "sp6.ratios",
  level: "sp6",
  name: text("Proporcje i stosunki", "Ratios and proportions"),
  modes: ["numeric", "choice4"],
  generate: (rng, tier) => (tier === 1 ? tier1(rng) : tier === 2 ? tier2(rng) : tier3(rng)),
};
